import { t, type MessageKey } from "@/lib/i18n/messages";
import { useAppContext } from "@/providers";
import type { NotificationPreferences } from "@/types/user";

interface NotificationPreferencesCardProps {
  preferences: NotificationPreferences | null;
  onToggle: (key: keyof NotificationPreferences, value: boolean) => void;
  loading: boolean;
}

const TOGGLES: { key: keyof NotificationPreferences; labelKey: MessageKey; descKey: MessageKey }[] = [
  { key: "new_initiatives", labelKey: "newInitiativesNotif", descKey: "newInitiativesNotifDesc" },
  { key: "contribution_requests", labelKey: "contributionRequestsNotif", descKey: "contributionRequestsNotifDesc" },
  { key: "initiative_updates", labelKey: "initiativeUpdatesNotif", descKey: "initiativeUpdatesNotifDesc" },
];

export function NotificationPreferencesCard({ preferences, onToggle, loading }: NotificationPreferencesCardProps) {
  const { locale } = useAppContext();

  return (
    <section className="bg-card p-6 rounded-lg shadow-sm border border-border">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
          <span className="material-icons-outlined">notifications_active</span>
        </div>
        <h2 className="text-lg font-bold">{t(locale, "notificationPreferences")}</h2>
      </div>
      <div className="space-y-3">
        {TOGGLES.map((toggle) => {
          const checked = preferences?.[toggle.key] ?? false;
          return (
            <div
              key={toggle.key}
              className="flex items-center justify-between p-4 bg-muted rounded-lg"
            >
              <div>
                <p className="font-medium">{t(locale, toggle.labelKey)}</p>
                <p className="text-xs text-foreground-secondary">
                  {t(locale, toggle.descKey)}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={checked}
                  disabled={loading}
                  onChange={() => onToggle(toggle.key, !checked)}
                />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
              </label>
            </div>
          );
        })}
      </div>
    </section>
  );
}
